import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isPresent } from "@apl-digital/utils";

import { IDiningOptionEnum } from "@/api/interfaces/configurationLayouts";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  selectContactInfo,
  selectHasContactInfo,
  selectIsContactInfoBound,
} from "@/store/contactInfo";
import { initializePayment, selectPaymentSession } from "@/store/payment";
import {
  bindContactInfo,
  bundleShoppingCart,
  selectCanEditShoppingCart,
  selectCurrentBarcodeWithoutChecksum,
  selectCurrentTransactionId,
  selectShoppingCartDingingOption,
  unbundleShoppingCart,
  updateTheatreResource,
} from "@/store/shoppingCart";
import {
  getTheatreResource,
  selectTheatreResource,
} from "@/store/theatreResource";
import { selectIsTheatreResourceBound } from "@/store/theatreResource/selectors";
import { selectUserGuid } from "@/store/user";
import { isStatus } from "@/store/utils";

type FlowErrorState =
  | "bindContactInfoError"
  | "theatreResourceError"
  | "bundleShoppingCartError"
  | "paymentLinkError";

type FlowState =
  | FlowErrorState
  | "idle"
  | "started"
  | "bindContactInfoCheckStarted"
  | "bindContactInfoOk"
  | "theatreResourceCheckStarted"
  | "theatreResourceNeeded"
  | "theatreResourceRefreshNeeded"
  | "theatreResourceOk"
  | "bundleShoppingCartStarted"
  | "bundleShoppingCartOk"
  | "paymentLinkStarted"
  | "paymentLinkOk"
  | "completed"
  | "error";

type OnErrorProps = {
  error: FlowErrorState;
};

type UsePayNowCheckoutFlowProps = {
  onError?: ({ error }: OnErrorProps) => void;
};

export type UsePayNowCheckoutFlowResults = {
  setFlowState: (state: FlowState) => void;
  resetFlow: () => void;
  startFlow: () => void;
  currentFlowState: FlowState;
  isComplete: boolean;
  paymentSession: ReturnType<typeof selectPaymentSession>;
};

export const usePayNowCheckoutFlow = ({
  onError,
}: UsePayNowCheckoutFlowProps): UsePayNowCheckoutFlowResults => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const transactionId = useAppSelector(selectCurrentTransactionId);
  const barcodeWithoutChecksum = useAppSelector(
    selectCurrentBarcodeWithoutChecksum,
  );
  const userGuid = useAppSelector(selectUserGuid);
  const theatreResource = useAppSelector(selectTheatreResource);
  const isTheatreResourceBound = useAppSelector(selectIsTheatreResourceBound);
  const contactInfo = useAppSelector(selectContactInfo);
  const hasContactInfo = useAppSelector(selectHasContactInfo);
  const isContactInfoBound = useAppSelector(selectIsContactInfoBound);
  const canEditShoppingCart = useAppSelector(selectCanEditShoppingCart);
  const diningOptionId = useAppSelector(selectShoppingCartDingingOption);
  const paymentSession = useAppSelector(selectPaymentSession);

  const [flowState, setFlowState] = React.useState<FlowState>("idle");

  const startFlow = () => {
    setFlowState("started");
  };

  const resetFlow = () => {
    setFlowState("idle");
  };

  useEffect(() => {
    switch (flowState) {
      case "started":
        setFlowState("bindContactInfoCheckStarted");

        break;

      case "bindContactInfoOk":
        if (diningOptionId === IDiningOptionEnum.DINE_IN) {
          setFlowState("theatreResourceCheckStarted");
        } else {
          setFlowState("theatreResourceOk");
        }

        break;

      case "theatreResourceOk":
        setFlowState("bundleShoppingCartStarted");

        break;

      case "bundleShoppingCartOk":
        setFlowState("paymentLinkStarted");

        break;

      case "paymentLinkOk":
        setFlowState("completed");

        break;

      // No default
    }
  }, [flowState]);

  useEffect(() => {
    switch (flowState) {
      case "bindContactInfoError":
        onError?.({ error: flowState });

        break;

      case "theatreResourceError":
        if (isPresent(transactionId)) {
          void dispatch(unbundleShoppingCart({ transactionId }))
            .unwrap()
            .catch(() => {
              // Report the original checkout error even if cleanup fails.
            })
            .finally(() => {
              onError?.({ error: flowState });
            });
        } else {
          onError?.({ error: flowState });
        }

        break;

      case "bundleShoppingCartError":
        onError?.({ error: flowState });

        break;

      case "paymentLinkError":
        onError?.({ error: flowState });

        break;

      // No default
    }
  }, [flowState]);

  useEffect(() => {
    if (flowState === "bindContactInfoCheckStarted") {
      if (!isPresent(transactionId) || !hasContactInfo) {
        setFlowState("bindContactInfoError");
      } else if (!canEditShoppingCart && isContactInfoBound) {
        setFlowState("bindContactInfoOk");
      } else if (hasContactInfo) {
        dispatch(bindContactInfo({ transactionId, body: contactInfo }))
          .unwrap()
          .then(() => {
            setFlowState("bindContactInfoOk");
          })
          .catch(() => {
            setFlowState("bindContactInfoError");
          });
      }
    }
  }, [flowState, canEditShoppingCart, transactionId, contactInfo]);

  useEffect(() => {
    if (flowState === "theatreResourceCheckStarted") {
      if (!isPresent(transactionId)) {
        setFlowState("theatreResourceError");
      } else if (!canEditShoppingCart && isTheatreResourceBound) {
        setFlowState("theatreResourceOk");
      } else if (isStatus(theatreResource, "succeeded")) {
        dispatch(
          updateTheatreResource({
            transactionId,
            theatreResourceId: theatreResource.state.ID,
          }),
        )
          .unwrap()
          .then(() => {
            setFlowState("theatreResourceOk");
          })
          .catch(() => {
            setFlowState("theatreResourceError");
          });
      } else if (isStatus(theatreResource, ["idle", "failed"])) {
        setFlowState("theatreResourceNeeded");
      } else if (isStatus(theatreResource, "stale")) {
        void dispatch(
          getTheatreResource({
            barcodeWithoutChecksum: theatreResource.state.Barcode,
          }),
        );
      }
    }
  }, [flowState, canEditShoppingCart, theatreResource]);

  useEffect(() => {
    if (flowState === "bundleShoppingCartStarted") {
      if (!isPresent(transactionId)) {
        setFlowState("bundleShoppingCartError");
      } else if (canEditShoppingCart) {
        dispatch(bundleShoppingCart({ transactionId }))
          .unwrap()
          .then(() => {
            setFlowState("bundleShoppingCartOk");
          })
          .catch(() => {
            setFlowState("bundleShoppingCartError");
          });
      } else {
        setFlowState("bundleShoppingCartOk");
      }
    }
  }, [flowState, canEditShoppingCart, transactionId]);

  useEffect(() => {
    if (flowState === "paymentLinkStarted") {
      if (!isPresent(transactionId) || !isPresent(barcodeWithoutChecksum)) {
        setFlowState("paymentLinkError");
      } else if (isStatus(paymentSession, ["idle", "failed"])) {
        dispatch(
          initializePayment({
            barcodeWithoutChecksum,
            returnLabel: t("cart_checkout_returnLabel"),
            confirmationEmail: contactInfo?.Email ?? undefined,
            customerGUID: userGuid,
          }),
        )
          .unwrap()
          .then(() => {
            setFlowState("paymentLinkOk");
          })
          .catch(() => {
            setFlowState("paymentLinkError");
          });
      }
    }
  }, [flowState, canEditShoppingCart, transactionId, paymentSession, userGuid]);

  return {
    resetFlow,
    startFlow,
    setFlowState,
    currentFlowState: flowState,
    isComplete: flowState === "completed",
    paymentSession,
  };
};
